import { useEffect, useState, useCallback } from 'react'
import { toast } from 'react-toastify'
import { Button } from '../../components/semantic-ui-antd-adapter'
import { Table } from 'antd'
import api from '../../api'
import { MatriculaModel } from '../../models/MatriculaModel'
import styled from '@emotion/styled'
import { CopyElement } from '../../components/CopyElement'
import { Link } from 'react-router-dom'

const CPF_INSTRUTOR = '136.717.741-34'

export function CertificadosPendentesEnvio() {
  const [loading, setLoading] = useState(false)
  const [matriculas, setMatriculas] = useState<MatriculaModel[]>([])
  const getCertificados = useCallback(async () => {
    try {
      setLoading(true)
      const response = await api.Certificados.getPendentes()
      setMatriculas(response)
    } catch (error) {
      const message =
        error.response?.data?.message ??
        'Ocorreu um erro ao buscar os certificados pendentes de envio'
      toast(message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    getCertificados()
  }, [])

  return (
    <Layout>
      <Container>
        <HeaderSection />
        <CertificadosTable data={matriculas} loading={loading} reload={getCertificados} />
      </Container>
    </Layout>
  )
}

function HeaderSection() {
  return (
    <HeaderRow>
      <Title>Certificados Pendentes de Envio</Title>
    </HeaderRow>
  )
}

function CertificadosTable({ data, loading, reload }) {
  const dfData = data.filter((matricula: MatriculaModel) => matricula.uf === 'DF')
  const mgData = data.filter((matricula: MatriculaModel) => matricula.uf === 'MG')

  const dfColumns = [
    {
      title: 'Nome',
      key: 'nome',
      render: (_: any, matricula: MatriculaModel) => (
        <Link
          to={`/cursos/${matricula?.curso?.id}/usuarios/${matricula?.aluno?.id}/acompanhamento?matriculaId=${matricula.id}`}
          style={{ textTransform: 'uppercase', fontWeight: 700, color: 'inherit' }}
        >
          {matricula?.aluno?.nome}
        </Link>
      ),
    },
    {
      title: 'CPF',
      key: 'cpf_formatado',
      render: (_: any, matricula: MatriculaModel) => (
        <CopyElement value={matricula?.aluno?.cpf}>
          <span>{matricula?.aluno?.cpf}</span>
        </CopyElement>
      ),
    },
    { title: 'Curso', dataIndex: ['curso', 'nomeReduzido'], key: 'curso' },
    { title: 'UF', dataIndex: 'uf', key: 'uf' },
    {
      title: 'Data Início',
      key: 'data_inicio',
      render: (_: any, matricula: MatriculaModel) => {
        const formattedDate = formatDate(matricula?.dataInicioCurso)
        return (
          <CopyElement value={formattedDate}>
            <span>{formattedDate}</span>
          </CopyElement>
        )
      },
    },
    {
      title: 'Data Fim',
      key: 'data_fim',
      render: (_: any, matricula: MatriculaModel) => {
        const formattedDate = formatDate(matricula?.dataFimCurso)
        return (
          <CopyElement value={formattedDate}>
            <span>{formattedDate}</span>
          </CopyElement>
        )
      },
    },
    {
      title: 'Código do certificado',
      key: 'certificado',
      render: (_: any, matricula: MatriculaModel) => (
        <CopyElement value={matricula?.identificacaoCertificadoRetornado ?? ''}>
          <span>{matricula?.identificacaoCertificadoRetornado}</span>
        </CopyElement>
      ),
    },
    {
      title: 'CPF do Instrutor',
      key: 'cpf_instrutor',
      render: (_: any) => (
        <CopyElement value={CPF_INSTRUTOR}>
          <span>{CPF_INSTRUTOR}</span>
        </CopyElement>
      ),
    },
    {
      title: 'Ações',
      key: 'action',
      render: (_: any, matricula: MatriculaModel) => (
        <Button onClick={() => handleConfirmarEnvio(matricula, reload)}>Confirmar envio</Button>
      ),
    },
  ]

  const mgColumns = [
    {
      title: 'Nome',
      key: 'nome',
      render: (_: any, matricula: MatriculaModel) => (
        <Link
          to={`/cursos/${matricula?.curso?.id}/usuarios/${matricula?.aluno?.id}/acompanhamento?matriculaId=${matricula.id}`}
          style={{ textTransform: 'uppercase', fontWeight: 700, color: 'inherit' }}
        >
          {matricula?.aluno?.nome}
        </Link>
      ),
    },
    {
      title: 'CPF',
      key: 'cpf_raw',
      render: (_: any, matricula: MatriculaModel) => {
        const cpfRaw = getRawValue(matricula?.aluno?.cpf)
        const cpfFormatado = formatCpf(matricula?.aluno?.cpf)
        return (
          <CopyElement value={cpfRaw}>
            <span>{cpfFormatado}</span>
          </CopyElement>
        )
      },
    },
    { title: 'Curso', dataIndex: ['curso', 'nomeReduzido'], key: 'curso' },
    { title: 'UF', dataIndex: 'uf', key: 'uf' },
    {
      title: 'Data Início',
      key: 'data_inicio',
      render: (_: any, matricula: MatriculaModel) => {
        const formattedDate = formatDate(matricula?.dataInicioCurso)
        const rawDate = getRawValue(formattedDate)
        return (
          <CopyElement value={rawDate}>
            <span>{formattedDate}</span>
          </CopyElement>
        )
      },
    },
    {
      title: 'Data Fim',
      key: 'data_fim',
      render: (_: any, matricula: MatriculaModel) => {
        const formattedDate = formatDate(matricula?.dataFimCurso)
        const rawDate = getRawValue(formattedDate)
        return (
          <CopyElement value={rawDate}>
            <span>{formattedDate}</span>
          </CopyElement>
        )
      },
    },
    {
      title: 'Código do certificado',
      key: 'certificado',
      render: (_: any, matricula: MatriculaModel) => (
        <CopyElement value={matricula?.identificacaoCertificadoRetornado ?? ''}>
          <span>{matricula?.identificacaoCertificadoRetornado}</span>
        </CopyElement>
      ),
    },
    {
      title: 'CPF do Instrutor',
      key: 'cpf_instrutor_raw',
      render: (_: any) => {
        const cpfInstrutorRaw = getRawValue(CPF_INSTRUTOR)
        return (
          <CopyElement value={cpfInstrutorRaw}>
            <span>{CPF_INSTRUTOR}</span>
          </CopyElement>
        )
      },
    },
    {
      title: 'Ações',
      key: 'action',
      render: (_: any, matricula: MatriculaModel) => (
        <Button onClick={() => handleConfirmarEnvio(matricula, reload)}>Confirmar envio</Button>
      ),
    },
  ]

  return (
    <TablesWrapper>
      <Section>
        <SectionHeader>
          <SectionHeaderLeft>
            <SectionTitle>DF</SectionTitle>
            <SectionMeta>{dfData.length} pendentes</SectionMeta>
          </SectionHeaderLeft>
          <SectionHeaderRight>
            <SystemLink href="https://adm.detran.df.gov.br/" target="_blank" rel="noreferrer">
              Sistema DF
            </SystemLink>
          </SectionHeaderRight>
        </SectionHeader>
        <TableWrapper>
          <Table columns={dfColumns} dataSource={dfData} loading={loading} rowKey="id" />
        </TableWrapper>
      </Section>

      <Section>
        <SectionHeader>
          <SectionHeaderLeft>
            <SectionTitle>MG</SectionTitle>
            <SectionMeta>{mgData.length} pendentes</SectionMeta>
          </SectionHeaderLeft>
          <SectionHeaderRight>
            <SystemLink
              href="https://empresas.detran.mg.gov.br/sdaf/paginas/ssc/login.asp"
              target="_blank"
              rel="noreferrer"
            >
              Sistema MG
            </SystemLink>
          </SectionHeaderRight>
        </SectionHeader>
        <TableWrapper>
          <Table columns={mgColumns} dataSource={mgData} loading={loading} rowKey="id" />
        </TableWrapper>
      </Section>
    </TablesWrapper>
  )
}

async function handleConfirmarEnvio(matricula: MatriculaModel, reload) {
  if (!confirm('Confirma o envio do certificado?')) return
  try {
    await api.Certificados.updateConfirmacaoEnvio(matricula.id)
    reload()
    toast('Certificado enviado com sucesso!')
    if (matricula?.identificacaoCertificado) {
      window.open(
        `https://app.newdriver.com.br/certificado/${matricula.identificacaoCertificado}`,
        '_blank'
      )
    }
  } catch (error) {
    const message =
      error.response?.data?.message ?? 'Ocorreu um erro ao confirmar o envio do certificado'
    toast(message)
  }
}

function getRawValue(value?: string) {
  return value?.replace(/\D/g, '') ?? ''
}

function formatDate(value?: string | Date) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value)

  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const year = String(date.getFullYear())
  return `${day}/${month}/${year}`
}

function formatCpf(value?: string) {
  const rawCpf = getRawValue(value)
  if (rawCpf.length !== 11) return value ?? ''
  return rawCpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
}

const Layout = styled.div`
  padding: 20px;
  background-color: whitesmoke;
  min-height: 100%;
`

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const Title = styled.span`
  font-size: 22px;
  color: #494d50;
  font-weight: 600;
`

const TablesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 0px 14px 0px 4px;
`

const SectionHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const SectionHeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const SectionTitle = styled.span`
  font-size: 18px;
  font-weight: 600;
  color: #494d50;
`

const SectionMeta = styled.span`
  font-size: 16px;
  color: #5a5f63;
`

const SystemLink = styled.a`
  font-size: 16px;
  color: #494d50;
  text-decoration: underline;
`

const TableWrapper = styled.div`
  padding: 8px;
  background: #ffffff;
  border-radius: 10px;
  width: 100%;
  min-width: 800px;

  .ant-table {
    font-size: 14px;
  }

  .ant-table-thead > tr > th {
    background: #ffffff;
    color: #585858;
    font-weight: normal;
    border-bottom: 1px solid #e7e3ec;
    padding: 12px 16px;
  }

  .ant-table-tbody > tr > td {
    border-bottom: 1px solid #e7e3ec;
    padding: 8px 16px;
  }

  .ant-table-tbody > tr:hover > td {
    background: #f5f5f5;
  }

  .ant-table-pagination {
    margin: 16px 0;
  }
`
